import React, { useState, useEffect, useCallback } from 'react';
import { Box, Text, VStack, Heading, Center, HStack, IconButton, useToast } from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import useAppointmentTypes from '../hooks/useAppointmentTypes';
import useBookAppointment from '../hooks/useBookAppointment';
import AppointmentsGroup from '../components/AppointmentsGroup';
import AppointmentSlots from '../components/AppointmentSlots';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import BookingBreadcrumb from '../components/BookingBreadcrumb';
import API from '../api/axiosConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import "../styles/pages/BookAppointment.css";

const BookAppointment = () => {
    const { appointmentTypes, isLoading, error, fetchAppointmentTypes } = useAppointmentTypes();
    const [selectedType, setSelectedType] = useState(() => sessionStorage.getItem('selectedType'));
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [slotsError, setSlotsError] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date(new Date().setDate(new Date().getDate() + 13)));
    const [typeName, setTypeName] = useState(null);

    const { bookAppointment, isBooking, bookingError } = useBookAppointment();
    const navigate = useNavigate();
    const toast = useToast();
    const location = useLocation();
    const selectedAppointment = location.state?.selectedAppointment;
    const isPlanned = selectedAppointment && selectedAppointment.AptStatus === "Planned";
    const isScheduledPlanned = selectedAppointment?.IsScheduledPlanned;

    const handleTabChange = () => {
        // Placeholder function: Add logic here if needed in the future
    };

    const handleSlotError = useCallback((err) => {
        if (err.response && err.response.status === 403) {
            const uuid = localStorage.getItem('UUID');
            toast({
                title: "Session Expired",
                description: "Your session has expired. You will be redirected to the login page.",
                status: "warning",
                duration: null,
                isClosable: true,
            });
            setTimeout(() => {
                window.location.href = uuid ? `/${uuid}` : "/";
            }, 2000);
        } else {
            console.error("Failed to load slots:", err);
            setSlotsError('Failed to load slots. Please try again.');
            sessionStorage.removeItem('slots');
        }
    }, [toast]);

    const fetchSlotsForPlannedAppointment = useCallback(async (isPlanned, _at, dateStart) => {
        const numberOfDays = 14;
        try {
            const response = await API.get(`/appointments/web/open/slots`, {
                params: { datestart: dateStart, numberofdays: numberOfDays, _at, isPlanned }
            });
            setSlots(response.data);
            sessionStorage.setItem('slots', JSON.stringify(response.data));
        } catch (err) {
            handleSlotError(err);
        } finally {
            setLoadingSlots(false);
        }
    }, [handleSlotError]);

    const fetchSlots = useCallback(async (type, dateStart) => {
        const numberOfDays = 14;
        try {
            const response = await API.get(`/appointments/web/open/slots`, {
                params: { datestart: dateStart, numberofdays: numberOfDays, _at: type }
            });
            setSlots(response.data);
            sessionStorage.setItem('slots', JSON.stringify(response.data));
        } catch (err) {
            handleSlotError(err);
        } finally {
            setLoadingSlots(false);
        }
    }, [handleSlotError]);

    useEffect(() => {
        if (!fetchAppointmentTypes) {
            console.error("fetchAppointmentTypes is not defined");
        } else {
            fetchAppointmentTypes();
        }
    }, [fetchAppointmentTypes]);

    useEffect(() => {
        const savedType = sessionStorage.getItem('selectedType');
        const savedSlots = sessionStorage.getItem('slots');
        const dateStart = startDate.toISOString().split('T')[0];
        
        if (selectedAppointment) {
            if (isPlanned || isScheduledPlanned) {
                setSlots([]);
                setLoadingSlots(true);
                fetchSlotsForPlannedAppointment(true, selectedAppointment.AptData, dateStart);
            } else {
                setSelectedType(savedType || null);
                setSlots([]);
                setLoadingSlots(true);
                if (savedType) fetchSlots(savedType, dateStart);
            }
        } else if (savedType) {
            setSelectedType(savedType);
            setSlots(savedSlots ? JSON.parse(savedSlots) : []);
            setLoadingSlots(true);
            fetchSlots(savedType, dateStart);
        } else if (savedSlots) {
            setSlots(JSON.parse(savedSlots));
        }
    }, [fetchSlots, fetchSlotsForPlannedAppointment, isPlanned, isScheduledPlanned, selectedAppointment, startDate]);

    const handleSelectCategory = (category) => {
        console.log("Selected category:", category);
        setSelectedCategory(category);
        setTypeName(null);
    };

    const handleSelectType = (token, typeName) => {
        console.log(typeName, "Selected type token:", token);
        setSelectedType(token);
        setTypeName(typeName);
        
        sessionStorage.setItem('selectedType', token);
        setSlots([]);
        setLoadingSlots(true);
        setSlotsError(null);
    
        const categoryIndex = appointmentTypes.findIndex(categoryItem =>
            categoryItem.types.some(type => type.token === token)
        );
        if (categoryIndex !== -1) {
            setSelectedTabIndex(categoryIndex);
            setSelectedCategory(appointmentTypes[categoryIndex].category);
        }
    
        const dateStart = startDate.toISOString().split('T')[0];
        fetchSlots(token, dateStart);
    };

    const handlePrevDates = () => {
        setLoadingSlots(true);
        setSlots([]);
        const newStartDate = new Date(startDate);
        newStartDate.setDate(newStartDate.getDate() - 14);
        const newEndDate = new Date(endDate);
        newEndDate.setDate(newEndDate.getDate() - 14);
        setStartDate(newStartDate);
        setEndDate(newEndDate);

        if (isPlanned || isScheduledPlanned) {
            fetchSlotsForPlannedAppointment(true, selectedAppointment.AptData, newStartDate.toISOString().split('T')[0]);
        } else {
            fetchSlots(selectedType, newStartDate.toISOString().split('T')[0]);
        }
    };

    const handleNextDates = () => {
        setLoadingSlots(true);
        setSlots([]);
        const newStartDate = new Date(startDate);
        newStartDate.setDate(newStartDate.getDate() + 14);
        const newEndDate = new Date(endDate);
        newEndDate.setDate(newEndDate.getDate() + 14);
        setStartDate(newStartDate);
        setEndDate(newEndDate);

        if (isPlanned || isScheduledPlanned) {
            fetchSlotsForPlannedAppointment(true, selectedAppointment.AptData, newStartDate.toISOString().split('T')[0]);
        } else {
            fetchSlots(selectedType, newStartDate.toISOString().split('T')[0]);
        }
    };

    const handleConfirmBooking = async (_pos, date, time) => {
        if (isBooking) return;

        console.log("Starting the booking or rescheduling process");

        let appointmentType = selectedType;
        let rescheduleSuccess = false;

        try {
            const isPlanned = selectedAppointment?.AptStatus === "Planned" && !selectedAppointment?.IsScheduledPlanned;
            const isScheduledPlanned = selectedAppointment?.IsScheduledPlanned;
            const isUpcoming = !isPlanned && !isScheduledPlanned && selectedAppointment?.AptStatus === "Scheduled";
            const isNewBooking = !selectedAppointment && !isPlanned && !isScheduledPlanned;
            
            if (isPlanned) {
                console.log("Handling Planned Appointment (New Booking):");
                // For planned appointments, pass AptData (the token)
                await bookAppointment(_pos, date, time, appointmentType, false, true, selectedAppointment.AptData);
                rescheduleSuccess = true;
                handleToast(true, "Booking Successful", "Your planned appointment has been booked successfully.");
            } else if (isScheduledPlanned) {
                console.log("Handling Scheduled Planned Appointment (Reschedule):");
                // For rescheduling, pass the full appointment object
                await bookAppointment(_pos, date, time, appointmentType, true, true, selectedAppointment);
                rescheduleSuccess = true;
                handleToast(true, "Rescheduling Successful", "Your scheduled planned appointment has been rescheduled successfully.");
            } else if (isUpcoming) {
                console.log("Handling Upcoming Appointment (Reschedule):");
                // For rescheduling, pass the full appointment object
                await bookAppointment(_pos, date, time, appointmentType, true, false, selectedAppointment);
                rescheduleSuccess = true;
                handleToast(true, "Rescheduling Successful", "Your upcoming appointment has been rescheduled successfully.");
            } else if (isNewBooking) {
                console.log("Handling New Regular Appointment (New Booking):");
                // For new bookings, appointmentType is sufficient
                await bookAppointment(_pos, date, time, appointmentType, false, false, null);
                rescheduleSuccess = true;
                handleToast(true, "Booking Successful", "Your new appointment has been booked successfully.");
            }

            navigate('/thank-you', { state: { rescheduleSuccess } });
        } catch (error) {
            console.error("Error during booking or rescheduling process:", error);
            handleToast(false, "Booking Failed", bookingError || "There was an error booking your appointment. Please try again.");
        }
    };

    const handleToast = (success, title, description) => {
        toast({
            title: success ? title : `${title} Failed`,
            description: success ? description : `There was an issue: ${description}`,
            status: success ? "success" : "error",
            duration: 5000,
            isClosable: true,
        });
    };

    const handleResetType = () => {
        setSelectedType(null);
        setTypeName(null);
        sessionStorage.removeItem('selectedType');
        sessionStorage.removeItem('slots');
        sessionStorage.removeItem('selectedAppointment');
    };

    const handleResetCategory = () => {
        setSelectedCategory(null);
        setSelectedType(null);
        setTypeName(null);
        sessionStorage.removeItem('selectedType');
        sessionStorage.removeItem('slots');
        sessionStorage.removeItem('selectedAppointment');
    };

    const formattedStartDate = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const formattedEndDate = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isLoading) return <LoadingSpinner />;
    if (error) return <ErrorAlert title="Error" description="Failed to load appointment types. Please try again." onClose={() => {}} />;

    return (
        <Box className="book-appointment-container">
            {slotsError && <ErrorAlert title="Error" description={slotsError} onClose={() => setSlotsError(null)} />}

            {!selectedType && !isPlanned && !isScheduledPlanned && !selectedCategory && (
                <Center className="select-type-heading">
                    <Heading as="h3" size="lg" fontWeight="bold" className="page-heading">
                        What type of care are you looking for?
                    </Heading>
                </Center>
            )}
    
            {!selectedCategory && !isPlanned && !isScheduledPlanned && !selectedType ? (
                <>
                    <BookingBreadcrumb 
                        category={selectedCategory} 
                        typeName={typeName} 
                        onResetType={handleResetType} 
                        onResetCategory={handleResetCategory} 
                    />
                    <VStack className="select-type-list">
                        {appointmentTypes.map((categoryItem, index) => (
                            <Box
                                key={index}
                                className="type-box"
                                onClick={() => handleSelectCategory(categoryItem.category)}
                            >
                                <Text className="type-text">{categoryItem.category}</Text>
                            </Box>
                        ))}
                    </VStack>
                </>
            ) : selectedCategory && !selectedType ? (
                <>
                    <BookingBreadcrumb 
                        category={selectedCategory} 
                        typeName={typeName} 
                        onResetType={handleResetType} 
                        onResetCategory={handleResetCategory} 
                    />
                    <VStack className="select-type-list">
                        {appointmentTypes
                            .find((item) => item.category === selectedCategory)
                            ?.types.map(({ typeName, token }, index) => (
                                <Box
                                    key={index}
                                    className="type-box"
                                    onClick={() => handleSelectType(token, typeName)}
                                >
                                    <Text className="type-text">{typeName}</Text>
                                </Box>
                            ))}
                    </VStack>
                </>
            ) : (
                <Box>
                    {selectedCategory && (
                        <>
                            <BookingBreadcrumb 
                                category={selectedCategory} 
                                typeName={typeName} 
                                onResetType={handleResetType} 
                                onResetCategory={handleResetCategory} 
                            />
                            <AppointmentsGroup
                                categories={appointmentTypes}
                                onSelect={(token, typeName) => handleSelectType(token, typeName)}
                                selectedType={selectedType}
                                selectedTabIndex={selectedTabIndex}
                                isClickable={!selectedAppointment}
                                onTabChange={handleTabChange}
                                compactView={true}
                            />
                        </>
                    )}
                    <HStack className="slots-header">
                        <Text className="providers-count">
                            {slots.length ? `${slots.length} providers available` : 'Loading providers...'}
                        </Text>
                        <HStack className="date-navigation">
                            <IconButton
                                icon={<FaChevronLeft />}
                                onClick={handlePrevDates}
                                isDisabled={startDate.toDateString() === new Date().toDateString()}
                                aria-label="Previous dates"
                                className="nav-btn"
                            />
                            <Text className="date-range">{`${formattedStartDate} – ${formattedEndDate}`}</Text>
                            <IconButton
                                icon={<FaChevronRight />}
                                onClick={handleNextDates}
                                aria-label="Next dates"
                                className="nav-btn"
                            />
                        </HStack>
                    </HStack>
    
                    {loadingSlots ? (
                        <LoadingSpinner message="Loading slots..." />
                    ) : (
                        <AppointmentSlots
                            slots={slots}
                            error={slotsError}
                            startDate={startDate}
                            onConfirmBooking={handleConfirmBooking}
                            isBooking={isBooking}
                            bookingError={bookingError}
                            selectedAppointment={selectedAppointment}
                            isPlanned={isPlanned}
                        />
                    )}
                </Box>
            )}
        </Box>
    );    
};

export default BookAppointment;